import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ResetPasscodeSession = {
  phoneE164: string;
  ownerUserId: string;
  tempAccessToken?: string;
  tempRefreshToken?: string;
};

type ResetPasscodeContextValue = {
  session: ResetPasscodeSession | null;
  setSession: (next: ResetPasscodeSession | null) => void;
  patchSession: (partial: Partial<ResetPasscodeSession>) => void;
  clearSession: () => void;
};

const ResetPasscodeContext = createContext<ResetPasscodeContextValue | null>(
  null,
);

export const ResetPasscodeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<ResetPasscodeSession | null>(null);

  const patchSession = useCallback((partial: Partial<ResetPasscodeSession>) => {
    setSession(prev => {
      if (!prev) return prev;
      return { ...prev, ...partial };
    });
  }, []);

  const clearSession = useCallback(() => {
    setSession(null);
  }, []);

  const value = useMemo(
    () => ({
      session,
      setSession,
      patchSession,
      clearSession,
    }),
    [session, patchSession, clearSession],
  );

  return (
    <ResetPasscodeContext.Provider value={value}>
      {children}
    </ResetPasscodeContext.Provider>
  );
};

export function useResetPasscode(): ResetPasscodeContextValue {
  const ctx = useContext(ResetPasscodeContext);
  if (!ctx) {
    throw new Error(
      'useResetPasscode must be used within ResetPasscodeProvider',
    );
  }
  return ctx;
}
